import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalCloseButton,
  Text,
  Button,
  FormControl,
  ModalBody,
  ModalFooter,
  Input,
  Switch,
} from '@chakra-ui/react';
import {
  transportRequestsApi,
  UpdateTransportRequest,
} from '@texas/api/endpoints/transportRequestApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { useForm, Controller } from 'react-hook-form';
import { ErrorDetails } from '../alert/ErrorDetails';
import { ErrorLabel } from '../ErrorLabel';
import { TexasFormLabel } from '../form/TexasFormLabel';
import { SharedDisclosureProps } from '../types';
import { transportRequestEvents } from './events';
import { request } from '@texas/utils/helpers/httpHelpers';
import 'react-datepicker/dist/react-datepicker.css';
import { useTranslation } from 'react-i18next';
import { SpinnerWithLabel } from '../SpinnerWithLabel';
import { UserAvatar } from '../users/UserAvatar';
import { TransportRequestsSummary } from '../shipment/TransportRequestsSummary';
import { FormCounter } from '../form/FormCounter';

interface Props extends SharedDisclosureProps {
  trId: number;
}

const maxLength = 200;

export function UpdateTransportRequestModal(props: Props) {
  const { t } = useTranslation();
  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      isCentered={true}
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pb={0}>
          {t('transportRequest.updateId', { id: props.trId })}
        </ModalHeader>
        <ModalCloseButton />
        <Inner
          trId={props.trId}
          onUpdate={() => {
            transportRequestEvents.onTransportUpdated.dispatch();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({ trId, onUpdate }: { trId: number; onUpdate: () => void }) {
  const { t } = useTranslation();

  const {
    control,
    register,
    handleSubmit,
    formState: { isSubmitting, isValid, errors },
    watch,
    reset,
  } = useForm<UpdateTransportRequest>({
    defaultValues: {
      asap: false,
      requestDispatchDate: new Date(),
      note: '',
    },
  });

  const { refetch, data, loading } = useApiResource(transportRequestsApi.get);

  useEffect(() => {
    refetch(trId);
  }, [refetch, trId]);

  useEffect(() => {
    if (!data) return;
    reset({
      asap: data.asapShipment,
      requestDispatchDate: new Date(data.requestedDispatchDate),
      note: data.note,
    });
  }, [data, reset]);

  const { request: updateReq, error: updateError } = useApiRequest(
    transportRequestsApi.update,
  );

  const performRequest = (data: UpdateTransportRequest) =>
    request(updateReq, [trId, data], () => onUpdate());

  return (
    <>
      <form onSubmit={handleSubmit(performRequest)}>
        <ModalBody>
          {updateError && <ErrorDetails error={updateError} />}

          {loading ? (
            <SpinnerWithLabel label={t('general.loadingData')} />
          ) : (
            <Flex gap={4} flexDir="column">
              {data && (
                <>
                  <TransportRequestsSummary
                    samplesCount={data.samplesInTransport}
                  />
                  <FormControl>
                    <TexasFormLabel>{t('shipment.receiver')}</TexasFormLabel>
                    <UserAvatar
                      name={data.receiverUser.name}
                      email={data.receiverUser.email}
                      label={data.receiverUser.label}
                      showTeams={false}
                    />
                  </FormControl>
                </>
              )}
              <FormControl>
                <TexasFormLabel>{t('general.commentIfAny')}</TexasFormLabel>
                <Input {...register('note', { maxLength })} variant="texas" />
                <FormCounter
                  length={watch('note').length}
                  maxLength={maxLength}
                />
              </FormControl>
              <FormControl gap={2}>
                <Flex
                  gap={2}
                  alignItems="center"
                  justifyContent="space-between"
                  p={2}
                  bg="texas.bg.800"
                  borderRadius="md"
                  _light={{
                    bg: 'gray.50',
                  }}
                >
                  <Text variant="small">{t('transportRequest.asap')}</Text>
                  <Controller
                    name="asap"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        isChecked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    )}
                  />
                </Flex>
              </FormControl>
              {!watch('asap') && (
                <FormControl isRequired={true}>
                  <TexasFormLabel>
                    {t('transportRequest.reqDispatchDate')}
                  </TexasFormLabel>
                  <Controller
                    control={control}
                    name="requestDispatchDate"
                    rules={{ required: true }}
                    render={({ field }) => (
                      <DatePicker
                        selected={field.value}
                        onChange={(date) => field.onChange(date)}
                        inline={true}
                        calendarStartDay={1}
                      />
                    )}
                  />
                  <ErrorLabel text={errors.requestDispatchDate?.message} />
                </FormControl>
              )}
            </Flex>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            type="submit"
            isLoading={isSubmitting || loading}
            isDisabled={isSubmitting || !isValid || loading}
          >
            {t('general.update')}
          </Button>
        </ModalFooter>
      </form>
    </>
  );
}
